import { Check, ChevronDown } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { usePatchMatterMutation } from '@/services/api/mattersService';
import { MatterType } from '@/services/types/client-intake-types';

import { BADGE_BASE_CLASSES } from './shared';

// CaseTypeEditBadge uses a dropdown, so it doesn't fit the standard useInlineEdit pattern perfectly
// because the "editing" state is the dropdown being open, and selection triggers save immediately.
// Keeping the original logic but moved to its own file.

interface CaseTypeEditBadgeProps {
  matterId: string;
  value: string;
  className?: string;
}

export const CaseTypeEditBadge: React.FC<CaseTypeEditBadgeProps> = ({
  matterId,
  value,
  className,
}) => {
  const [patchMatter, { isLoading }] = usePatchMatterMutation();

  const allTypes = Object.values(MatterType);
  const currentType = (value as MatterType) || MatterType.UNKNOWN;

  const handleSelect = async (type: MatterType) => {
    if (type === currentType) return;

    try {
      await patchMatter({
        matter_id: matterId,
        patch: { matter_type: type },
      }).unwrap();
      toast.success('Case type updated');
    } catch (error) {
      toast.error('Failed to update case type');
    }
  };

  const displayValue =
    value?.toLowerCase() === 'unknown'
      ? 'Unknown Case Type'
      : value || 'Unknown Case Type';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          disabled={isLoading}
          className={cn(
            BADGE_BASE_CLASSES,
            'flex items-center gap-1 pr-1 data-[state=open]:bg-zinc-50',
            className,
          )}
          type="button"
        >
          <span className="font-medium">{displayValue}</span>
          <ChevronDown className="h-3 w-3 text-zinc-400 transition-transform duration-200" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[220px]">
        <div className="max-h-[300px] overflow-y-auto">
          {allTypes.map((type) => (
            <DropdownMenuItem
              key={type}
              onSelect={() => handleSelect(type)}
              disabled={isLoading}
              className={cn(
                'text-xs cursor-pointer',
                currentType === type && 'bg-blue-50 text-blue-700 font-medium',
              )}
            >
              <span className="flex-1 truncate">
                {type === MatterType.UNKNOWN ? 'Unknown Case Type' : type}
              </span>
              {currentType === type && (
                <Check className="ml-2 h-3 w-3 text-blue-600" />
              )}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
